import { CustomState } from "@hardware/Store/Reducers";
import setFieldValue from "@hardware/Store/State/CampaignForm/SetFieldValue";
import { CampaignFormContext } from "@hardware/Widgets/CampaignTwoColumn/CampaignForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => {
    const fieldName = "companyName";
    return {
        fieldName,
        fieldValue: state.campaignForm.fieldValues[fieldName],
    };
};

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FormFieldStyles {
    textField?: TextFieldPresentationProps;
}

export const formFieldStyles: FormFieldStyles = {};

const styles = formFieldStyles;

const CompanyName: React.FC<Props> = ({ fields, fieldName, fieldValue, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const campaignFormContext = React.useContext(CampaignFormContext);
    const [fieldErrorMessage, setFieldErrorMessage] = React.useState("");

    React.useEffect(() => {
        campaignFormContext.validators[fieldName] = validateField;
        if (typeof fieldValue !== "undefined") {
            validateField();
        }
    }, [fieldValue]);

    React.useEffect(() => {
        campaignFormContext.validators[fieldName] = validateField;
    }, [isRequired]);

    const validateField = () => {
        const errorMessage = isRequired && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setFieldErrorMessage(errorMessage);
        return !errorMessage;
    };

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: fieldName, value: event.target.value });
    };

    return (
        <TextField
            {...styles.textField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={fieldValue || ""}
            error={fieldErrorMessage}
            onChange={fieldChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyName as any) as any,
    definition: {
        displayName: "Company Name",
        group: "Campaign" as any,
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Company Name",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Smith's Tools & Hardware",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
